import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import {
    BuyerPaymentsDetailAssociatedSuppliersRequest,
    BuyerPaymentsDetailAssociatedSuppliersResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/list-suppliers-associated-with-payment';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { startWith } from 'rxjs/operators';
import { TransferDetailInput, TransferDetailResponse } from '../../model/models';

@Injectable({ providedIn: 'root' })
export class ListSuppliersAssociatedWithPaymentService extends ApiService<
    BuyerPaymentsDetailAssociatedSuppliersRequest,
    BuyerPaymentsDetailAssociatedSuppliersResponse
> {
    public requestType = BuyerPaymentsDetailAssociatedSuppliersRequest;
    public responseType = BuyerPaymentsDetailAssociatedSuppliersResponse;
    public endpointName = 'buyer/BuyerPaymentsList/ListSuppliersAssociatedWithPayment';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName =
                        'buyer/BuyerPaymentsList/ListSuppliersAssociatedWithPayment';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: BuyerPaymentsDetailAssociatedSuppliersRequest
    ): { query: string } | BuyerPaymentsDetailAssociatedSuppliersRequest {
        if (this._isPayLaterEnabled) {
            const input: TransferDetailInput = {
                transferId: req.batchKey
            };
            const query = jsonToGraphQLQuery({
                transferDetail: {
                    __args: { input },
                    rows: {
                        supplier: true,
                        supplierKey: true
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public responseMapper(
        res:
            | GraphQLResponse<TransferDetailResponse>
            | BuyerPaymentsDetailAssociatedSuppliersResponse
    ): BuyerPaymentsDetailAssociatedSuppliersResponse {
        if (this._isPayLaterEnabled) {
            const { transferDetail } = (res as GraphQLResponse<TransferDetailResponse>).data;
            return {
                rows: transferDetail.rows.map((transfer) => {
                    return {
                        supplierKey: transfer.supplierKey,
                        supplierLegalName: transfer.supplier
                    };
                })
            } as BuyerPaymentsDetailAssociatedSuppliersResponse;
        }
        return res as BuyerPaymentsDetailAssociatedSuppliersResponse;
    }
}
